import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import type { BreakdownScope } from '../data/api';
import type { BreakdownTab } from '../data/mediaBreakdownData';
import type { MediaKpi } from '../data/types';
import { MediaFatigue } from './MediaFatigue';
import { MediaTabs } from './MediaTabs';
import { OverPerforming } from './OverPerforming';
import { UnderPerforming } from './UnderPerforming';

const PRINT_SECTIONS: Array<{ tab: BreakdownTab; title: string }> = [
  { tab: 'over', title: 'Over Performing' },
  { tab: 'under', title: 'Under Performing' },
  { tab: 'fatigue', title: 'Media Fatigue' },
];

interface BreakdownContentProps {
  tab: BreakdownTab;
  kpi: MediaKpi;
  scope: BreakdownScope;
  enabled: boolean;
}

function BreakdownContent({ tab, kpi, scope, enabled }: BreakdownContentProps): ReactNode {
  if (tab === 'over') return <OverPerforming kpi={kpi} scope={scope} enabled={enabled} />;
  if (tab === 'under') return <UnderPerforming kpi={kpi} scope={scope} enabled={enabled} />;
  return <MediaFatigue kpi={kpi} scope={scope} enabled={enabled} />;
}

export function PerformanceBreakdown({
  kpi,
  scope,
  enabled,
}: {
  kpi: MediaKpi;
  scope: BreakdownScope;
  enabled: boolean;
}): ReactNode {
  const [tab, setTab] = useState<BreakdownTab>('over');

  return (
    <>
      <Box
        data-testid="screen-performance-breakdown"
        sx={{ mb: 4, '@media print': { display: 'none' } }}
      >
        <MediaTabs active={tab} onChange={setTab} />
        <BreakdownContent tab={tab} kpi={kpi} scope={scope} enabled={enabled} />
      </Box>

      <Box
        data-testid="print-performance-breakdown"
        sx={{
          display: 'none',
          '@media print': {
            display: 'block',
            breakAfter: 'page',
            zoom: 0.6,
          },
        }}
      >
        {PRINT_SECTIONS.map(({ tab: printTab, title }) => (
          <Box key={printTab} sx={{ breakBefore: 'page' }}>
            <Typography
              component="h2"
              sx={{ mb: 3, fontSize: 18, fontWeight: 700, color: 'text.primary' }}
            >
              {title}
            </Typography>
            <BreakdownContent tab={printTab} kpi={kpi} scope={scope} enabled={enabled} />
          </Box>
        ))}
      </Box>
    </>
  );
}
